import { apiFetch } from '../utils/api';

export const PROPOSAL_AUTHORIZATION_NOTICE_VERSION =
  'proposal-automation-consent-v1-2026-07-25';

export const PROPOSAL_PURPOSE_SCOPES = [
  'proposal_drafting',
  'compliance_export',
] as const;

export const PROPOSAL_OPERATION_SCOPES = [
  'collect',
  'analyze',
  'persist',
  'retrieve',
  'export',
  'external_ai',
] as const;

export const PROPOSAL_SOURCE_SCOPES = [
  'direct_input',
  'company_profile',
] as const;

export const PROPOSAL_DATA_CATEGORIES = [
  'business_profile',
  'performance',
  'product_service',
  'certification',
  'intellectual_property',
  'financial',
  'contact',
  'personal_data',
] as const;

export const PROPOSAL_EXTERNAL_PROCESSORS = [
  'anthropic',
  'perplexity',
] as const;

export type CompanyAuthorityBasis =
  | 'company_representative'
  | 'delegated_officer';

export interface CompanyDataAuthorization {
  id: string;
  company_id: string;
  notice_version: string;
  status: string;
  effective_status: string;
  legal_basis: string;
  purpose_scopes: string[];
  operation_scopes: string[];
  source_scopes: string[];
  data_categories: string[];
  sharing_scope: string;
  external_processors: string[];
  retention_days: number;
  retention_until: string;
  granted_at: string;
  expires_at: string;
}

export interface CompanyDataAuthorizationList {
  total: number;
  items: CompanyDataAuthorization[];
}

export interface ProposalAuthorizationCreatePayload {
  notice_version: string;
  legal_basis: 'consent';
  authority_basis: CompanyAuthorityBasis;
  purpose_scopes: string[];
  operation_scopes: string[];
  source_scopes: string[];
  data_categories: string[];
  allowed_domains: string[];
  sharing_scope: 'approved_processors';
  external_processors: string[];
  retention_days: number;
  retention_policy: 'authorization_expiry_or_withdrawal';
  expires_in_days: number;
  confirmed_company_authority: true;
  confirmed_notice: true;
}

export interface ApiResponseError {
  status: number;
  code?: string;
  message: string;
}

const extractValidationMessage = (detail: unknown): string | undefined => {
  if (!Array.isArray(detail)) return undefined;

  const messages = detail
    .map((item) => {
      if (
        item &&
        typeof item === 'object' &&
        'msg' in item &&
        typeof item.msg === 'string'
      ) {
        return item.msg;
      }
      return null;
    })
    .filter((item): item is string => Boolean(item));

  return messages.length > 0 ? messages.join(' ') : undefined;
};

const hasEvery = (actual: string[] | undefined, required: readonly string[]) => {
  const values = new Set(actual || []);
  return required.every((item) => values.has(item));
};

export function isProposalAuthorizationCompatible(
  authorization: CompanyDataAuthorization,
  now: Date = new Date(),
): boolean {
  const expiresAt = Date.parse(authorization.expires_at);
  const retentionUntil = Date.parse(authorization.retention_until);
  const nowTime = now.getTime();

  return (
    authorization.status === 'active' &&
    authorization.effective_status === 'active' &&
    authorization.notice_version ===
      PROPOSAL_AUTHORIZATION_NOTICE_VERSION &&
    authorization.legal_basis === 'consent' &&
    Number.isFinite(expiresAt) &&
    Number.isFinite(retentionUntil) &&
    expiresAt > nowTime &&
    retentionUntil > nowTime &&
    authorization.sharing_scope === 'approved_processors' &&
    hasEvery(authorization.purpose_scopes, PROPOSAL_PURPOSE_SCOPES) &&
    hasEvery(authorization.operation_scopes, PROPOSAL_OPERATION_SCOPES) &&
    hasEvery(authorization.source_scopes, PROPOSAL_SOURCE_SCOPES) &&
    hasEvery(authorization.data_categories, PROPOSAL_DATA_CATEGORIES) &&
    hasEvery(
      authorization.external_processors,
      PROPOSAL_EXTERNAL_PROCESSORS,
    )
  );
}

export function buildProposalAuthorizationPayload(
  authorityBasis: CompanyAuthorityBasis,
): ProposalAuthorizationCreatePayload {
  return {
    notice_version: PROPOSAL_AUTHORIZATION_NOTICE_VERSION,
    legal_basis: 'consent',
    authority_basis: authorityBasis,
    purpose_scopes: [...PROPOSAL_PURPOSE_SCOPES],
    operation_scopes: [...PROPOSAL_OPERATION_SCOPES],
    source_scopes: [...PROPOSAL_SOURCE_SCOPES],
    data_categories: [...PROPOSAL_DATA_CATEGORIES],
    allowed_domains: [],
    sharing_scope: 'approved_processors',
    external_processors: [...PROPOSAL_EXTERNAL_PROCESSORS],
    retention_days: 365,
    retention_policy: 'authorization_expiry_or_withdrawal',
    expires_in_days: 365,
    confirmed_company_authority: true,
    confirmed_notice: true,
  };
}

export async function readApiResponseError(
  response: Response,
  fallback: string,
): Promise<ApiResponseError> {
  let message = fallback;
  let code: string | undefined;

  try {
    const body = await response.json();
    const detail = body?.detail;
    const appError = body?.error;

    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = extractValidationMessage(detail) || fallback;
    } else if (detail && typeof detail === 'object') {
      code = typeof detail.code === 'string' ? detail.code : undefined;
      message =
        typeof detail.message === 'string' ? detail.message : fallback;
    } else if (typeof appError === 'string') {
      message = appError;
    } else if (appError && typeof appError === 'object') {
      code = typeof appError.code === 'string' ? appError.code : undefined;
      message =
        typeof appError.message === 'string'
          ? appError.message
          : typeof appError.detail === 'string'
            ? appError.detail
            : fallback;
    } else if (typeof body?.message === 'string') {
      message = body.message;
      code = typeof body?.code === 'string' ? body.code : undefined;
    }
  } catch {
    // Keep the caller-provided, user-safe fallback for non-JSON responses.
  }

  return { status: response.status, code, message };
}

export function asApiResponseError(
  caught: unknown,
): ApiResponseError | null {
  if (
    typeof caught !== 'object' ||
    caught === null ||
    !('status' in caught) ||
    typeof caught.status !== 'number' ||
    !('message' in caught) ||
    typeof caught.message !== 'string'
  ) {
    return null;
  }

  return {
    status: caught.status,
    code:
      'code' in caught && typeof caught.code === 'string'
        ? caught.code
        : undefined,
    message: caught.message,
  };
}

export function isAuthorizationRequiredError(error: ApiResponseError): boolean {
  return (
    error.status === 428 ||
    error.code === 'company_data_authorization_required' ||
    (error.status === 409 && error.code === 'scope_conflict')
  );
}

export async function listCompanyDataAuthorizations(
  companyId: string,
  signal?: AbortSignal,
): Promise<CompanyDataAuthorizationList> {
  const response = await apiFetch(
    `/api/companies/${encodeURIComponent(companyId)}/data-authorizations?limit=100`,
    { method: 'GET' },
    signal,
  );
  if (!response.ok) {
    throw await readApiResponseError(
      response,
      '기업 자료 처리 동의 이력을 불러오지 못했습니다.',
    );
  }
  return response.json();
}

export async function createProposalAuthorization(
  companyId: string,
  authorityBasis: CompanyAuthorityBasis,
): Promise<CompanyDataAuthorization> {
  const response = await apiFetch(
    `/api/companies/${encodeURIComponent(companyId)}/data-authorizations`,
    {
      method: 'POST',
      body: JSON.stringify(buildProposalAuthorizationPayload(authorityBasis)),
    },
  );
  if (!response.ok) {
    throw await readApiResponseError(
      response,
      '기업 자료 처리 동의를 등록하지 못했습니다.',
    );
  }
  return response.json();
}

export async function withdrawCompanyDataAuthorization(
  companyId: string,
  authorizationId: string,
  reason: string,
): Promise<CompanyDataAuthorization> {
  const response = await apiFetch(
    `/api/companies/${encodeURIComponent(
      companyId,
    )}/data-authorizations/${encodeURIComponent(
      authorizationId,
    )}/withdraw`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() }),
    },
  );
  if (!response.ok) {
    throw await readApiResponseError(
      response,
      '기업 자료 처리 동의를 철회하지 못했습니다.',
    );
  }
  return response.json();
}
